import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';

// "Registrar tiempo" (H3): el agente carga horas al ticket sin salir de Desk. Las horas se
// guardan en Veta Horas (mismo proyecto del ticket) con todas sus reglas: asignación,
// vigencia, topes, tickets cancelados y tope de horas sin ticket.

interface TicketTimeEntry { id: string; userName: string; mine: boolean; dateKey: string; hours: number; activity: string }
interface TicketTime {
  number: string;
  totalHours: number;
  entries: TicketTimeEntry[];
  initiativeId: string | null;
  initiativeCode: string | null;
  estimateHours: number | null;
}

const QUICK = [0.5, 1, 1.5, 2, 4];
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmt = (n: number) => String(Math.round(n * 100) / 100);
const shortDate = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

export function TimePanel({ number, initiativeId, initiativeCode, open, status, release, canRelease }: {
  number: string; initiativeId: string | null; initiativeCode: string | null; open: boolean;
  status?: string; release?: { reason: string; by: { name: string }; until: string } | null; canRelease?: boolean;
}) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(todayKey);
  const [hours, setHours] = useState('1');
  const [activity, setActivity] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const time = useQuery({
    queryKey: ['ticket-time', number],
    queryFn: () => api<TicketTime>(`/tickets/${number}/time`),
    enabled: Boolean(initiativeId),
    retry: false,
    staleTime: 30_000
  });

  const log = useMutation({
    mutationFn: () => api<{ projectCode: string; capStatus?: { level: string; consumed?: number; maxHours?: number | null; pct?: number | null } | null }>(
      `/tickets/${number}/time`,
      { method: 'POST', body: JSON.stringify({ date, hours: Number(hours.replace(',', '.')), activity }) }
    ),
    onSuccess: (res) => {
      const h = Number(hours.replace(',', '.'));
      const cap = res.capStatus;
      const capText = cap && (cap.level === 'alert' || cap.level === 'over') && cap.maxHours
        ? ` El proyecto lleva ${cap.consumed?.toFixed(1)} de ${cap.maxHours} h.`
        : '';
      setMessage({ tone: 'ok', text: `Se registraron ${fmt(h)} h en ${res.projectCode} (Veta Horas).${capText}` });
      setActivity('');
      void queryClient.invalidateQueries({ queryKey: ['ticket-time', number] });
    },
    onError: (err) => setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'No se pudo registrar el tiempo.' })
  });

  const hoursNum = Number(hours.replace(',', '.'));
  const valid = Number.isFinite(hoursNum) && hoursNum > 0 && hoursNum <= 24;
  const notConfigured = time.error instanceof ApiError && time.error.code === 'VETA_NOT_CONFIGURED';
  const field = 'h-10 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm';

  return (
    <details className="group rounded-lg border-2 border-accent bg-white">
      <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-2 px-4 [&::-webkit-details-marker]:hidden">
        <span className="flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"
            className="-rotate-90 text-muted transition-transform group-open:rotate-0"><path d="M6 9l6 6 6-6" /></svg>
          <h2 id="time-title" className="text-sm font-semibold">Registrar tiempo</h2>
        </span>
        <span className="text-xs text-muted">
          {time.data ? `${fmt(time.data.totalHours)} h registradas · ` : ''}{initiativeCode ? `va a ${initiativeCode}` : 'va a Veta Horas'}
        </span>
      </summary>
      <div className="px-4 pb-4">

      {!initiativeId ? (
        <p className="rounded-md bg-paper px-3 py-2 text-sm text-muted">
          Asígnale una iniciativa (Veta) en Propiedades para registrar tiempo en este ticket.
        </p>
      ) : notConfigured ? (
        <p className="rounded-md bg-paper px-3 py-2 text-sm text-muted">{time.error?.message}</p>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); setMessage(null); if (valid) log.mutate(); }}>
          {!open && <ClosedNote number={number} status={status} release={release ?? null} canRelease={Boolean(canRelease)} />}
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1 text-xs text-muted">Fecha
              <input type="date" value={date} max={todayKey()} onChange={e => setDate(e.target.value)} required className={field} />
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted">Horas
              <input type="text" inputMode="decimal" value={hours} onChange={e => setHours(e.target.value)}
                aria-invalid={!valid} className={`${field} font-semibold ${valid ? '' : 'border-[#B91C1C]'}`} />
            </label>
          </div>
          <div role="group" aria-label="Horas rápidas" className="flex flex-wrap gap-1.5">
            {QUICK.map(q => (
              <button key={q} type="button" aria-pressed={hoursNum === q} onClick={() => setHours(String(q))}
                className={`h-8 rounded-full border px-3 text-xs ${hoursNum === q ? 'border-accent bg-accent-soft font-semibold text-accent' : 'border-[#D6D3CD] hover:bg-paper'}`}>
                {q}
              </button>
            ))}
          </div>
          <label className="flex flex-col gap-1 text-xs text-muted">Qué hiciste (opcional)
            <input type="text" value={activity} maxLength={200} onChange={e => setActivity(e.target.value)}
              placeholder="p. ej. Pruebas en QAS" className={field} />
          </label>
          <button type="submit" disabled={!valid || log.isPending}
            className="h-11 rounded-md bg-accent text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50">
            {log.isPending ? 'Registrando…' : `Registrar ${valid ? fmt(hoursNum) : ''} h`}
          </button>
          {message && (
            <p role={message.tone === 'error' ? 'alert' : 'status'}
              className={`rounded-md px-3 py-2 text-sm ${message.tone === 'error' ? 'bg-[#FBEAEA] text-[#8F1D1D]' : 'bg-accent-soft text-accent-dark'}`}>
              {message.text}
            </p>
          )}
        </form>
      )}

      {initiativeId && time.data && (
        <div className="mt-4 border-t border-[#EFEDE9] pt-3 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="font-semibold">Registrado en este ticket</span>
            <span className="font-semibold">{fmt(time.data.totalHours)} h</span>
          </div>
          {time.data.estimateHours ? (
            <p className="mt-1 text-xs text-muted">
              Estimado {fmt(time.data.estimateHours)} h · {Math.round((time.data.totalHours / time.data.estimateHours) * 100)} % consumido
            </p>
          ) : null}
          {time.data.entries.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-semibold text-accent">Ver detalle ({time.data.entries.length})</summary>
              <ul className="mt-2 flex max-h-56 flex-col gap-1.5 overflow-y-auto">
                {time.data.entries.map(e => (
                  <li key={e.id} className="flex justify-between gap-3 text-xs">
                    <span className="min-w-0 truncate text-[#3F3D39]">
                      {shortDate(e.dateKey)} · {e.mine ? 'Tú' : e.userName}{e.activity ? ` · ${e.activity}` : ''}
                    </span>
                    <span className="flex-none font-semibold">{fmt(e.hours)} h</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      </div>
    </details>
  );
}

/** Etapa 2.2: un ticket cerrado no admite horas, salvo que el líder o el admin lo liberen (7 días, con motivo). */
function ClosedNote({ number, status, release, canRelease }: {
  number: string; status?: string; release: { reason: string; by: { name: string }; until: string } | null; canRelease: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api(`/tickets/${number}/hours-release`, { method: 'POST', body: JSON.stringify({ reason }) }),
    onSuccess: () => { setOpen(false); void queryClient.invalidateQueries({ queryKey: ['ticket', number] }); }
  });
  const active = release && new Date(release.until) > new Date();
  if (status !== 'closed') return <p className="text-xs text-muted">El ticket ya no está abierto: Veta no acepta horas en tickets cancelados, rechazados o convertidos.</p>;
  if (active) return <p className="rounded-md bg-[#FFF4E5] px-3 py-2 text-xs text-[#6A3A00]">Ticket cerrado con horas permitidas hasta el {new Date(release!.until).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })} ({release!.by.name}: “{release!.reason}”).</p>;
  return (
    <div className="flex flex-col gap-2 rounded-md bg-paper px-3 py-2 text-xs text-muted">
      <p>El ticket está cerrado: no admite horas nuevas.{canRelease ? '' : ' Si hubo trabajo después del cierre, pide al líder de servicio que lo libere.'}</p>
      {canRelease && !open && <button type="button" className="w-fit text-accent underline" onClick={() => setOpen(true)}>Permitir horas 7 días</button>}
      {canRelease && open && (
        <div className="flex flex-col gap-2">
          <input className="h-9 rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm text-ink" placeholder="Motivo (queda en el historial)" value={reason} onChange={e => setReason(e.target.value)} />
          {m.error && <p className="text-[#8F1D1D]">{(m.error as Error).message}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={reason.trim().length < 10 || m.isPending} onClick={() => m.mutate()} className="h-8 rounded-md bg-accent px-3 text-xs font-semibold text-white disabled:opacity-50">Permitir</button>
            <button type="button" onClick={() => setOpen(false)} className="h-8 rounded-md border border-[#D6D3CD] bg-white px-3 text-xs">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
